import { NavigateFunction } from 'react-router-dom';

import { createAsyncThunk } from '@reduxjs/toolkit';

import { defaultActionProcess } from 'commons/utils/defaultActionProcess';
import { RootReducerInterface } from 'redux-stores/reducers';
import {
  setInboundInvoice,
  setWaitingApprovalInvoiceList,
} from 'redux-stores/reducers/inboundInvoiceReducer';
import { INBOUND_ROUTES } from 'routes/constants';
import { swipeRxWmsApiInboundInvoice } from 'services/apis/SwipeRxWmsApiInboundInvoice';
import {
  InboundInvoiceCompleteApprovalRequest,
  InboundInvoicePrepopulatedData,
  PostCreateInboundInvoiceRequest,
  PostUpdateInboundInvoiceRequest,
} from 'services/apis/SwipeRxWmsApiInboundInvoice/type';

export const getInProgressInboundInvoiceAction = createAsyncThunk(
  'inboundInvoice/getInProgressInboundInvoiceAction',
  async (_, { dispatch }) => {
    await defaultActionProcess(async () => {
      const data =
        await swipeRxWmsApiInboundInvoice.getInProgressInboundInvoice();
      dispatch(setInboundInvoice(data));
    }, dispatch);
    return undefined;
  },
);

export const getWaitingApprovalInboundInvoice = createAsyncThunk(
  'inboundInvoice/getWaitingApprovalInboundInvoice',
  async (
    _params: {
      inboundTaskNumber: string;
      invoiceNumber: string;
      notFoundAction: () => void;
    },
    { dispatch },
  ) => {
    const { inboundTaskNumber, invoiceNumber, notFoundAction } = _params;

    defaultActionProcess(async () => {
      try {
        const response =
          await swipeRxWmsApiInboundInvoice.getWaitingApprovalInboundInvoice({
            inbound_task_number: inboundTaskNumber,
            invoice_number: invoiceNumber,
          });
        dispatch(setInboundInvoice(response));
      } catch (error) {
        // error from server / invoice simply not found
        notFoundAction();
        throw new Error('Invoice not found');
      }
    }, dispatch);
  },
);

export const getWaitingApprovalInvoiceList = createAsyncThunk(
  'inboundInvoice/getWaitingApprovalInvoiceList',
  async (_, { dispatch }) => {
    defaultActionProcess(async () => {
      const data =
        await swipeRxWmsApiInboundInvoice.getWaitingApprovalInvoiceList();
      dispatch(setWaitingApprovalInvoiceList(data));
    }, dispatch);
    return undefined;
  },
);

export const createInboundInvoiceAction = createAsyncThunk(
  'inboundInvoice/createInboundInvoiceAction',
  async (
    _params: {
      payload: PostCreateInboundInvoiceRequest;
      navigate?: NavigateFunction;
    },
    { dispatch },
  ) => {
    const { payload, navigate } = _params;
    defaultActionProcess(async () => {
      const data =
        await swipeRxWmsApiInboundInvoice.createInboundInvoice(payload);
      dispatch(setInboundInvoice(data));
      if (navigate) navigate(INBOUND_ROUTES.LIST);
    }, dispatch);
    return undefined;
  },
);

export const updateInboundInvoiceAction = createAsyncThunk(
  'inboundInvoice/updateInboundInvoiceAction',
  async (
    _params: {
      payload: InboundInvoicePrepopulatedData;
      navigate?: NavigateFunction;
    },
    { dispatch, getState },
  ) => {
    defaultActionProcess(async () => {
      const { payload, navigate } = _params;
      const { inboundInvoice } = (getState() as RootReducerInterface)
        .inboundInvoice;
      if (!inboundInvoice) {
        throw new Error('Invoice does not exist!');
      }
      const processedParams: PostUpdateInboundInvoiceRequest = {
        inbound_task_number: inboundInvoice?.inbound_task_number,
        invoice_number: payload.invoice_number,
        invoice_date: new Date(payload.invoice_date),
      };
      const data =
        await swipeRxWmsApiInboundInvoice.updateInboundInvoice(processedParams);
      dispatch(setInboundInvoice(data));
      if (navigate) navigate(INBOUND_ROUTES.LIST);
    }, dispatch);
    return undefined;
  },
);

export const confirmInboundInvoiceAction = createAsyncThunk(
  'inboundInvoice/confirmInboundInvoiceAction',
  async (postAction: () => void, { dispatch }) => {
    defaultActionProcess(async () => {
      await swipeRxWmsApiInboundInvoice.postConfirmInvoice();
      postAction();
    }, dispatch);
  },
);

export const completeInboundInvoiceApprovalAction = createAsyncThunk(
  'inboundInvoice/completeInboundInvoiceApprovalAction',
  async (
    params: {
      payload: InboundInvoiceCompleteApprovalRequest;
      postAction: () => void;
    },
    { dispatch },
  ) => {
    const { payload, postAction } = params;
    defaultActionProcess(async () => {
      await swipeRxWmsApiInboundInvoice.postCompleteApproval(payload);
      postAction();
    }, dispatch);
  },
);
